import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartPulse, Lightbulb, Loader2, ArrowRight, Send } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useBusinessMetrics } from '../../../hooks/useBusinessMetrics';
// Re-cableado a lealify-base (/analytics/rewards-performance) — reemplaza Elasticsearch.
import { analyticsApi } from '../../../api/analyticsApi';
import { detectLoyaltyModel } from '../utils/loyaltyModel';
import { RUTA_CAMPANAS, prefillReactivacion, prefillRecordarPuntos, prefillCumpleanos } from '../utils/campaignActions';
import CountUp from '../../../components/ui/CountUp';

/**
 * Panel superior de la página de análisis del negocio.
 * Muestra un "score de salud" del programa y un insight accionable,
 * ADAPTADO al modelo de uso real del negocio (sellos / puntos-canje / nuevo).
 * Fase 1: usa solo datos ya disponibles (sin tocar el backend).
 */
const ProgramHealthPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const { metrics, loading } = useBusinessMetrics(currentUser?.businessId || undefined);

  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [topReward, setTopReward] = useState<string | null>(null);
  const [rewLoaded, setRewLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!currentUser?.businessId) return;
      try {
        const data = await analyticsApi.rewardsPerformance(10000); // histórico completo
        if (!alive) return;
        setTotalRedemptions(data?.totalRedemptions || 0);
        setTopReward(data?.topRewards?.[0]?.title || null);
      } catch {
        /* silencioso: el panel funciona igual sin recompensas */
      } finally {
        if (alive) setRewLoaded(true);
      }
    };
    load();
    return () => { alive = false; };
  }, [currentUser?.businessId]);

  if (loading || !rewLoaded) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 flex justify-center items-center min-h-[140px] border border-line">
        <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-3" />
        <span className="text-content-3 text-sm">Evaluando la salud de tu programa...</span>
      </div>
    );
  }

  const info = detectLoyaltyModel({
    totalVisits: metrics.totalVisits,
    totalPointsGiven: metrics.totalPointsGiven,
    totalRedemptions,
    loyaltyMode: currentUser?.loyaltyMode,
  });
  const esPC = info.model === 'puntos-canje';
  const esNuevo = info.model === 'nuevo';

  const tasaCanje = metrics.uniqueCustomers > 0 ? (totalRedemptions / metrics.uniqueCustomers) * 100 : 0;

  // Score 0-100 (Fase 1, con datos disponibles). El score "real" con clientes
  // activos del mes llega en Fase 2 cuando el backend exponga ese dato.
  let score: number | null;
  if (esNuevo) {
    score = null;
  } else if (esPC) {
    score = Math.round(Math.min(100, tasaCanje * 1.0 + (totalRedemptions > 0 ? 25 : 0)));
  } else {
    score = Math.round(Math.min(100, 0.7 * (metrics.retentionRate || 0) + 0.3 * Math.min(100, tasaCanje * 4)));
  }

  const scoreColor = score === null ? 'var(--c-content-3)' : score >= 60 ? '#16a34a' : score >= 35 ? '#d97706' : '#dc2626';
  const scoreLabel = score === null ? 'Recopilando datos' : score >= 60 ? '🟢 Saludable' : score >= 35 ? '🟡 A vigilar' : '🔴 Necesita atención';

  // Insight accionable adaptado al modelo, conectado a una campaña dirigida.
  type Cta = { label: string; to: string; state?: any; primary?: boolean };
  let insight: React.ReactNode;
  const ctas: Cta[] = [];
  if (esNuevo) {
    insight = (
      <>Tu programa apenas arranca (<b>{metrics.totalVisits}</b> visitas registradas).
      Sigue dando de alta clientes y registrando visitas; en cuanto tengas más datos verás aquí tus tendencias, horas pico y segmentos de clientes.</>
    );
    ctas.push({ label: 'Registrar cliente', to: '/dashboard/register-client', primary: true });
  } else if (esPC) {
    insight = (
      <>Tu programa funciona por <b>acumulación de puntos para canje</b> (no premia visitas).
      Llevas <b>{totalRedemptions.toLocaleString('es-MX')}</b> canjes{topReward ? <> y tu premio estrella es <b>«{topReward}»</b></> : ''}.
      Recuérdale a tus clientes que tienen puntos esperando a ser canjeados.</>
    );
    ctas.push({ label: 'Recordar canjear puntos', to: RUTA_CAMPANAS, state: { prefill: prefillRecordarPuntos() }, primary: true });
  } else {
    const recurrentes = metrics.multiVisitClients || 0;
    const unaVisita = metrics.oneVisitClients || 0;
    insight = (
      <><b>{recurrentes.toLocaleString('es-MX')}</b> clientes ya volvieron 2 o más veces ({(metrics.retentionRate || 0).toFixed(0)}% de retención).
      {unaVisita > 0 ? <> Tienes <b>{unaVisita.toLocaleString('es-MX')}</b> que se quedaron en 1 sola visita — una campaña de regreso puede ayudar a recuperarlos.</> : ''}</>
    );
    ctas.push({ label: 'Campaña a clientes inactivos (30+ días)', to: RUTA_CAMPANAS, state: { prefill: prefillReactivacion() }, primary: true });
  }
  // CTA extra: felicitar a los cumpleañeros del mes (el público lo resuelve la campaña).
  if (!esNuevo) {
    ctas.push({ label: 'Felicitar cumpleañeros del mes', to: RUTA_CAMPANAS, state: { prefill: prefillCumpleanos() } });
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* Salud del programa */}
      <div className="lg:col-span-3 bg-surface shadow rounded-lg p-5 border border-line">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-3 flex items-center">
            <HeartPulse className="h-4 w-4 mr-1.5 text-rose-500" /> Salud del programa
          </h3>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${info.badgeClass}`}>{info.label}</span>
        </div>
        <div className="flex items-end gap-4">
          <div className="text-5xl font-display font-extrabold leading-none" style={{ color: scoreColor }}>
            {score === null ? '–' : <CountUp value={score} />}
          </div>
          <div className="flex-1 pb-1">
            <div className="text-sm font-semibold" style={{ color: scoreColor }}>{scoreLabel}</div>
            <div className="h-2.5 rounded-full bg-surface-2 overflow-hidden mt-1.5">
              <span className="block h-full rounded-full transition-[width] duration-300"
                style={{ width: `${score ?? 0}%`, background: scoreColor }} />
            </div>
            <div className="text-[11px] text-content-3 mt-1.5">
              {esPC ? 'Basado en canjes y actividad de tu programa de puntos.' : 'Combina tu retención de clientes y tasa de canje.'}
            </div>
          </div>
        </div>
        <div className="bg-surface-2 rounded-lg px-3.5 py-2.5 text-[13px] text-content-2 mt-3">
          {esPC ? (
            <><b>{totalRedemptions.toLocaleString('es-MX')}</b> canjes históricos · <b>{(metrics.uniqueCustomers || 0).toLocaleString('es-MX')}</b> clientes en tu base.</>
          ) : (
            <><b>{(metrics.uniqueCustomers || 0).toLocaleString('es-MX')}</b> clientes · <b>{(metrics.totalVisits || 0).toLocaleString('es-MX')}</b> visitas registradas.</>
          )}
        </div>
      </div>

      {/* Insight accionable */}
      <div className="lg:col-span-2 bg-surface shadow rounded-lg p-5 border-l-4 border-accent border-t border-r border-b border-line">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-3 flex items-center mb-2">
          <Lightbulb className="h-4 w-4 mr-1.5 text-amber-500" /> Insight de la semana
        </h3>
        <p className="text-sm leading-relaxed text-content mb-3">{insight}</p>
        <div className="flex flex-wrap gap-2">
          {ctas.map((cta, i) => (
            <Link key={i} to={cta.to} state={cta.state}
              className={cta.primary
                ? 'inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3.5 py-2 rounded-md'
                : 'inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline px-2 py-2'}>
              {cta.primary ? <Send className="h-4 w-4" /> : null}{cta.label}{cta.primary ? null : <ArrowRight className="h-4 w-4" />}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgramHealthPanel;
